import { useEffect, useState } from 'react';
import api from '../lib/api';

const ROLES = ['admin', 'sales_rep', 'viewer'];

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ full_name:'', email:'', password:'', role:'sales_rep' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editRole, setEditRole] = useState('');

  const load = () => {
    setLoading(true);
    api.get('/users')
      .then(r => setUsers(r.data.users))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    setError('');
    if (!form.full_name.trim()) return setError('Full name is required.');
    if (!form.email.trim()) return setError('Email is required.');
    if (form.password.length < 8) return setError('Password must be at least 8 characters.');
    setSaving(true);
    try {
      await api.post('/users', form);
      setShowForm(false);
      setForm({ full_name:'', email:'', password:'', role:'sales_rep' });
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create user.');
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (userId, role) => {
    await api.patch(`/users/${userId}`, { role });
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, role } : u));
    setEditingId(null);
  };

  const handleDelete = async (userId, name) => {
    if (!window.confirm(`Delete user ${name}? This cannot be undone.`)) return;
    await api.delete(`/users/${userId}`);
    setUsers(prev => prev.filter(u => u.id !== userId));
  };

  const roleBadge = r => {
    const map = { admin:'badge-blue', sales_rep:'badge-green', viewer:'badge-gray' };
    return <span className={`badge ${map[r]||'badge-gray'}`}>{r.replace('_',' ')}</span>;
  };

  return (
    <>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div style={{ fontSize:13, color:'var(--text-secondary)' }}>{users.length} users</div>
        <button className="btn btn-primary" onClick={() => { setShowForm(true); setError(''); }}>+ Add user</button>
      </div>

      {/* Add User Form */}
      {showForm && (
        <div className="card" style={{ marginBottom:16 }}>
          <div className="section-title" style={{ marginBottom:14 }}>New user</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <label className="form-label">Full name *</label>
              <input className="form-input" value={form.full_name}
                onChange={e => setForm({...form, full_name:e.target.value})}
                placeholder="e.g. Jane Smith" />
            </div>
            <div>
              <label className="form-label">Email *</label>
              <input className="form-input" type="email" value={form.email}
                onChange={e => setForm({...form, email:e.target.value})}
                placeholder="jane@company.com" />
            </div>
            <div>
              <label className="form-label">Password * (min 8 characters)</label>
              <input className="form-input" type="password" value={form.password}
                onChange={e => setForm({...form, password:e.target.value})}
                placeholder="••••••••" />
            </div>
            <div>
              <label className="form-label">Role</label>
              <select className="form-input" value={form.role}
                onChange={e => setForm({...form, role:e.target.value})}>
                {ROLES.map(r => <option key={r} value={r}>{r.replace('_',' ')}</option>)}
              </select>
            </div>
          </div>
          {error && <div className="form-error" style={{ marginTop:8 }}>{error}</div>}
          <div style={{ display:'flex', gap:8, marginTop:14 }}>
            <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>
              {saving ? 'Creating…' : 'Create user'}
            </button>
            <button className="btn" onClick={() => { setShowForm(false); setError(''); }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="card" style={{ padding:0 }}>
        {loading
          ? <div style={{ padding:24, fontSize:13, color:'var(--text-tertiary)' }}>Loading…</div>
          : <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th><th>Email</th><th>Role</th><th>Created</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div className="avatar" style={{ width:28, height:28, fontSize:10 }}>
                          {u.avatar_initials || '?'}
                        </div>
                        <span style={{ fontWeight:500 }}>{u.full_name}</span>
                      </div>
                    </td>
                    <td style={{ color:'var(--text-secondary)', fontSize:12 }}>{u.email}</td>
                    <td>
                      {editingId === u.id
                        ? <select
                            value={editRole}
                            onChange={e => setEditRole(e.target.value)}
                            onBlur={() => handleRoleChange(u.id, editRole)}
                            autoFocus
                            style={{ fontSize:12, border:'0.5px solid var(--border-md)',
                                     borderRadius:'var(--radius)', padding:'2px 6px',
                                     background:'var(--bg-secondary)', color:'var(--text)' }}
                          >
                            {ROLES.map(r => <option key={r} value={r}>{r.replace('_',' ')}</option>)}
                          </select>
                        : <span onClick={() => { setEditingId(u.id); setEditRole(u.role); }}
                            style={{ cursor:'pointer' }} title="Click to change role">
                            {roleBadge(u.role)}
                          </span>
                      }
                    </td>
                    <td style={{ color:'var(--text-tertiary)', fontSize:12 }}>
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                    <td>
                      <button
                        onClick={() => handleDelete(u.id, u.full_name)}
                        style={{ background:'none', border:'none', cursor:'pointer',
                                 color:'var(--danger)', fontSize:12 }}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
        }
      </div>

      <div className="card" style={{ marginTop:14 }}>
        <div className="section-title">Role permissions</div>
        <table className="data-table">
          <thead><tr><th>Role</th><th>Can do</th></tr></thead>
          <tbody>
            <tr><td><span className="badge badge-blue">admin</span></td><td style={{ fontSize:13 }}>Full access — manage users, all data, all settings</td></tr>
            <tr><td><span className="badge badge-green">sales rep</span></td><td style={{ fontSize:13 }}>Create and edit contacts, accounts, deals, log activities</td></tr>
            <tr><td><span className="badge badge-gray">viewer</span></td><td style={{ fontSize:13 }}>Read-only — can view all data but cannot create or edit</td></tr>
          </tbody>
        </table>
      </div>
    </>
  );
}